import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { cn } from "../../lib/utils";
import { TeamCodeBadge, type TeamCodeBadgeVariant } from "./TeamCodeBadge";

type TeamIdentityHeaderProps = {
  teamCode: string;
  teamName: string;
  teamPrimaryColor?: string | null;
  teamSecondaryColor?: string | null;
  opponentCode?: string | null;
  floorPrice?: number | null;
  lastTradePrice?: number | null;
  lastTradeTrend?: "UP" | "DOWN" | "FLAT" | null;
  lastTradeLabel?: string;
  roundText?: string | null;
  seriesScore?: string | null;
  streakEntries?: string[] | null;
  teamState?: string | null;
  showStatus?: boolean;
  showStreak?: boolean;
  streakVariant?: "inline" | "tokens";
  badgeSize?: number;
  badgeVariant?: TeamCodeBadgeVariant;
  badgeFlagCountryCode?: string | null;
  className?: string;
  nameClassName?: string;
  metaClassName?: string;
};

export function TeamIdentityHeader({
  teamCode,
  teamName,
  teamPrimaryColor,
  teamSecondaryColor,
  opponentCode,
  floorPrice,
  lastTradePrice,
  lastTradeTrend,
  lastTradeLabel = "Last",
  roundText,
  seriesScore,
  streakEntries,
  teamState,
  showStatus = true,
  showStreak = true,
  streakVariant = "inline",
  badgeSize = 52,
  badgeVariant,
  badgeFlagCountryCode,
  className,
  nameClassName,
  metaClassName,
}: TeamIdentityHeaderProps) {
  const isEliminated = teamState === "ELIMINATED";
  const isChampion = teamState === "CHAMPION";
  const statusLabel = isChampion ? "CHAMP" : null;
  const statusStyle = "bg-brand/20 text-brand";
  const normalizedStreak = (streakEntries ?? []).map((entry) => entry.toUpperCase());
  const TrendIcon =
    lastTradeTrend === "UP"
      ? ArrowUp
      : lastTradeTrend === "DOWN"
        ? ArrowDown
        : lastTradeTrend === "FLAT"
          ? Minus
          : null;
  const trendLabel =
    lastTradeTrend === "UP"
      ? "Last trade up"
      : lastTradeTrend === "DOWN"
        ? "Last trade down"
        : lastTradeTrend === "FLAT"
          ? "Last trade unchanged"
          : undefined;
  const trendClass =
    lastTradeTrend === "UP"
      ? "text-success"
      : lastTradeTrend === "DOWN"
        ? "text-danger"
        : "text-foreground-muted";

  return (
    <div className={cn("flex min-w-0 items-center gap-2.5 md:gap-3", className)}>
      <div className="flex shrink-0 items-center justify-center overflow-visible">
        <TeamCodeBadge
          code={teamCode}
          size={badgeSize}
          variant={badgeVariant}
          flagCountryCode={badgeFlagCountryCode}
          primaryColor={teamPrimaryColor ?? null}
          secondaryColor={teamSecondaryColor ?? null}
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          {showStatus && statusLabel && (
            <span
              className={cn(
                "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider md:text-xs",
                statusStyle,
              )}
            >
              {statusLabel}
            </span>
          )}
          <span className={cn("truncate text-lg font-bold text-foreground md:text-lg", nameClassName)}>
            {teamName}
          </span>
          {(opponentCode || lastTradePrice != null) && (
            <div className="ml-auto flex shrink-0 flex-col items-end gap-1">
              {opponentCode && (
                <span className="rounded-full bg-background-raised px-2 py-0.5 text-[10px] uppercase text-foreground-muted md:text-xs">
                  VS {opponentCode}
                </span>
              )}
              {lastTradePrice != null && (
                <span className="font-mono text-[10px] font-semibold uppercase tracking-wide text-foreground-muted md:text-xs">
                  {lastTradeLabel} {lastTradePrice.toFixed(0)}
                  {TrendIcon && (
                    <TrendIcon
                      className={cn("ml-1 inline h-3 w-3 align-[-0.12em]", trendClass)}
                      aria-label={trendLabel}
                    />
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        <div
          className={cn(
            "mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-foreground-subtle md:text-sm",
            metaClassName,
          )}
        >
          {typeof floorPrice === "number" && <span>FLOOR {Math.round(floorPrice)}</span>}
          {typeof floorPrice === "number" && roundText && <span className="text-foreground-faint">•</span>}
          {roundText && <span className="uppercase tracking-wide">{roundText}</span>}
          {(roundText || typeof floorPrice === "number") && seriesScore && (
            <span className="text-foreground-faint">•</span>
          )}
          {seriesScore && <span>{seriesScore}</span>}
        </div>

        {showStreak && normalizedStreak.length > 0 && streakVariant === "tokens" && (
          <div className="mt-2 flex items-center gap-1">
            {normalizedStreak.map((entry, index) => {
              const token = entry.startsWith("W") ? "W" : "L";
              return (
                <span
                  key={`${teamCode}-identity-streak-token-${index}`}
                  className={cn(
                    "inline-flex h-5 min-w-5 items-center justify-center rounded border px-1 font-mono text-[10px] font-semibold",
                    token === "W"
                      ? "border-success/35 bg-success/10 text-success"
                      : "border-danger/35 bg-danger/10 text-danger",
                  )}
                >
                  {token}
                </span>
              );
            })}
          </div>
        )}

        {showStreak && normalizedStreak.length > 0 && streakVariant === "inline" && (
          <div className="mt-1 flex items-center gap-1 text-[11px] uppercase tracking-wide md:text-xs">
            {normalizedStreak.map((result, index) => {
              const resultColor = result.startsWith("W")
                ? "text-success"
                : result.startsWith("L")
                  ? "text-danger"
                  : "text-foreground-muted";
              return (
                <span key={`${result}-${index}`} className="inline-flex items-center gap-1">
                  {index > 0 && <span className="text-foreground-faint">•</span>}
                  <span className={resultColor}>{result}</span>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
